/** Familiars: a little companion that greets the witch and makes the app feel like home. */

export interface Familiar {
  typeSlug: string;
  /** Required when typeSlug is "custom", e.g. "axolotl". */
  customType?: string | null;
  name: string;
}

export const FAMILIAR_NAME_MAX = 40;

export function validateFamiliar(f: Familiar): string[] {
  const errors: string[] = [];
  const name = f.name.trim();
  if (!name) errors.push("Your familiar needs a name.");
  if (name.length > FAMILIAR_NAME_MAX)
    errors.push(`Names can be up to ${FAMILIAR_NAME_MAX} characters.`);
  if (!f.typeSlug) errors.push("Choose a familiar type.");
  if (f.typeSlug === "custom") {
    const custom = (f.customType ?? "").trim();
    if (!custom) errors.push("Tell us what kind of creature your familiar is.");
    if (custom.length > FAMILIAR_NAME_MAX)
      errors.push(`Creature types can be up to ${FAMILIAR_NAME_MAX} characters.`);
  }
  return errors;
}

export type TimeOfDay = "dawn" | "day" | "dusk" | "night";

export function timeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 9) return "dawn";
  if (hour >= 9 && hour < 17) return "day";
  if (hour >= 17 && hour < 21) return "dusk";
  return "night";
}

const OPENERS: Record<TimeOfDay, string[]> = {
  dawn: ["Up with the sun?", "The dew is still on the herbs.", "A fresh dawn, full of promise."],
  day: ["The cauldron's warm.", "A fine day for a little magic.", "Shall we brew something?"],
  dusk: ["The veil grows thin at dusk.", "Candles lit, kettle on.", "Twilight suits you."],
  night: ["The moon is keeping watch.", "Night magic is the best magic.", "Can't sleep either?"],
};

const MOON_NOTES: Record<string, string> = {
  new_moon: "It's a new moon, perfect for setting intentions.",
  full_moon: "The moon is full tonight. Charge your stones!",
  waxing_crescent: "The moon is growing; so are your plans.",
  waning_crescent: "The moon is waning. A good time to let go.",
};

/**
 * A short, welcoming greeting from the witch's familiar.
 * `pickIndex` keeps it deterministic in tests (defaults to the day of month).
 */
export function familiarGreeting(input: {
  familiar: Familiar | null;
  witchName?: string | null;
  hour: number;
  moonPhase?: string | null;
  pickIndex?: number;
}): string {
  const tod = timeOfDay(input.hour);
  const options = OPENERS[tod];
  const opener = options[(input.pickIndex ?? new Date().getDate()) % options.length]!;
  const who = input.witchName ? `, ${input.witchName}` : "";
  const moon = input.moonPhase ? MOON_NOTES[input.moonPhase] : undefined;
  const speaker = input.familiar ? `${input.familiar.name} says: ` : "";
  return `${speaker}Welcome back${who}. ${opener}${moon ? ` ${moon}` : ""}`;
}
